import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle, UserPlus, UserMinus } from 'lucide-react';
import toast from 'react-hot-toast';
import { resultsApi } from '@/api/endpoints/results';
import { participantsApi } from '@/api/endpoints/participants';
import { StageCompleter } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { cn } from '@/utils/cn';

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const STAGE2_QUALIFIED_KEY = ['stage2-qualified-ids'] as const;

export const QualificationPage = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [actionStudentId, setActionStudentId] = useState<string | null>(null);

  const { data: completers, isLoading: loadingCompleters } = useQuery({
    queryKey: ['combined-stage1'],
    queryFn: () => resultsApi.getCombinedStage1(),
  });

  const { data: qualifiedIds = [], isLoading: loadingQualified } = useQuery({
    queryKey: STAGE2_QUALIFIED_KEY,
    queryFn: () => participantsApi.getStage2QualifiedIds(),
  });

  const qualifiedStudentIds = useMemo(() => new Set(qualifiedIds), [qualifiedIds]);

  const filteredCompleters = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return completers ?? [];
    return (completers ?? []).filter((c) => {
      const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
      return (
        fullName.includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.school?.name.toLowerCase().includes(q) ?? false)
      );
    });
  }, [completers, search]);

  const promoteMutation = useMutation({
    mutationFn: (studentId: string) => participantsApi.promoteToStage2([studentId]),
    onMutate: async (studentId) => {
      await queryClient.cancelQueries({ queryKey: STAGE2_QUALIFIED_KEY });
      const previous = queryClient.getQueryData<string[]>(STAGE2_QUALIFIED_KEY);
      if (!previous?.includes(studentId)) {
        queryClient.setQueryData<string[]>(STAGE2_QUALIFIED_KEY, [...(previous ?? []), studentId]);
      }
      return { previous };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['magic-links'] });
      queryClient.invalidateQueries({ queryKey: STAGE2_QUALIFIED_KEY });
      setActionStudentId(null);
      if (result.promoted > 0) {
        toast.success('Uczeń zakwalifikowany do Etapu 2');
      } else if (result.skipped > 0) {
        toast.success('Uczeń był już zakwalifikowany');
      } else {
        toast.error('Uczeń nie spełnia warunków kwalifikacji');
      }
    },
    onError: (_err, _id, context) => {
      queryClient.setQueryData(STAGE2_QUALIFIED_KEY, context?.previous);
      setActionStudentId(null);
      toast.error('Błąd kwalifikacji');
    },
  });

  const demoteMutation = useMutation({
    mutationFn: (studentId: string) => participantsApi.demoteFromStage2(studentId),
    onMutate: async (studentId) => {
      await queryClient.cancelQueries({ queryKey: STAGE2_QUALIFIED_KEY });
      const previous = queryClient.getQueryData<string[]>(STAGE2_QUALIFIED_KEY);
      queryClient.setQueryData<string[]>(
        STAGE2_QUALIFIED_KEY,
        (previous ?? []).filter((id) => id !== studentId),
      );
      return { previous };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['magic-links'] });
      queryClient.invalidateQueries({ queryKey: STAGE2_QUALIFIED_KEY });
      setActionStudentId(null);
      toast.success(
        result.deleted > 0
          ? 'Usunięto kwalifikację do Etapu 2'
          : 'Brak oczekującej kwalifikacji do usunięcia',
      );
    },
    onError: (_err, _id, context) => {
      queryClient.setQueryData(STAGE2_QUALIFIED_KEY, context?.previous);
      setActionStudentId(null);
      toast.error('Błąd usuwania kwalifikacji');
    },
  });

  const handlePromote = (completer: StageCompleter) => {
    setActionStudentId(completer.id);
    promoteMutation.mutate(completer.id);
  };

  const handleDemote = (completer: StageCompleter) => {
    if (!confirm(`Usunąć kwalifikację ${completer.firstName} ${completer.lastName} do Etapu 2?`)) {
      return;
    }
    setActionStudentId(completer.id);
    demoteMutation.mutate(completer.id);
  };

  const isLoading = loadingCompleters || loadingQualified;
  const qualifiedCount = qualifiedStudentIds.size;
  const uniqueStudentsWithResults = new Set(completers?.map((c) => c.id)).size;
  const totalResults = completers?.length ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Kwalifikacja do Etapu 2</h2>
        <p className="mt-1 text-sm text-gray-500">
          Wyniki z obu terminów Etapu 1 połączone w jedną tabelę. Możesz ręcznie zarządzać kwalifikacją uczniów.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Wyniki z Etapu 1 (oba terminy)</p>
            <p className="mt-1 text-3xl font-bold text-gray-900">{totalResults}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Zakwalifikowani do Etapu 2</p>
            <p className="mt-1 text-3xl font-bold text-green-600">{qualifiedCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Niezakwalifikowani (z wynikiem)</p>
            <p className="mt-1 text-3xl font-bold text-gray-600">
              {Math.max(0, uniqueStudentsWithResults - qualifiedCount)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Łączona tabela wyników Etapu 1</CardTitle>
            <p className="mt-1 text-sm text-gray-500">
              Sortowanie: wynik malejąco, czas rosnąco. Remisy rozstrzyga czas od kliknięcia „Rozpocznij test”.
              Kwalifikacja dotyczy ucznia — jeśli ma kilka wyników (różne terminy), status jest wspólny.
            </p>
          </div>
          <Input
            placeholder="Szukaj ucznia, szkoły, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
          />
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSpinner size="md" className="py-10" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Termin</TableHead>
                  <TableHead>Uczeń</TableHead>
                  <TableHead>Szkoła</TableHead>
                  <TableHead className="text-right">Wynik</TableHead>
                  <TableHead className="text-right">Czas</TableHead>
                  <TableHead>Kwalifikacja</TableHead>
                  <TableHead className="text-right">Akcja</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompleters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-gray-500">
                      {search ? 'Brak wyników dla podanej frazy.' : 'Brak ukończonych testów Etapu 1.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCompleters.map((completer, index) => {
                    const isQualified = qualifiedStudentIds.has(completer.id);
                    const isBusy = actionStudentId === completer.id;
                    return (
                      <TableRow
                        key={completer.sessionId ?? `${completer.id}-${completer.term ?? 1}-${index}`}
                        className={cn(
                          isQualified && 'bg-green-50 hover:bg-green-100/80',
                          completer.disqualified && 'opacity-70',
                        )}
                      >
                        <TableCell className="text-gray-400">{index + 1}</TableCell>
                        <TableCell>
                          <span className="inline-flex rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                            Termin {completer.term ?? 1}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">
                          <span className="inline-flex flex-wrap items-center gap-2">
                            {completer.firstName} {completer.lastName}
                            {isQualified && (
                              <span className="inline-flex items-center rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                                Etap 2
                              </span>
                            )}
                            {completer.disqualified && (
                              <span className="text-xs font-normal text-red-600">(dyskwalifikacja)</span>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {completer.school?.name ?? '—'}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-primary-600">
                          {completer.score ?? '—'}
                        </TableCell>
                        <TableCell className="text-right text-gray-500">
                          {formatDuration(completer.durationFromStartClickMs)}
                        </TableCell>
                        <TableCell>
                          {isQualified ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                              Zakwalifikowany
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                              <XCircle className="h-3.5 w-3.5 shrink-0" />
                              Niezakwalifikowany
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {isQualified ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleDemote(completer)}
                              disabled={isBusy}
                            >
                              {isBusy ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                <span className="inline-flex items-center gap-1">
                                  <UserMinus className="h-3.5 w-3.5" />
                                  Usuń kwalifikację
                                </span>
                              )}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handlePromote(completer)}
                              disabled={isBusy || completer.disqualified}
                            >
                              {isBusy ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                <span className="inline-flex items-center gap-1">
                                  <UserPlus className="h-3.5 w-3.5" />
                                  Kwalifikuj
                                </span>
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
